/**
 * HUD — HTML overlay on top of the Three.js canvas.
 * Handles: scoreboard, commentary, aim instructions, result flashes.
 */

export class HUD {
  private _root: HTMLElement

  constructor(hudEl: HTMLElement) {
    this._root = hudEl
    this._root.innerHTML = ''
    this._root.style.cssText = `
      position:fixed; top:0; left:0; width:100%; height:100%;
      pointer-events:none; z-index:10;
      font-family:'Segoe UI',Tahoma,sans-serif;
    `
  }

  clear(): void {
    this._root.innerHTML = ''
  }

  showScoreboard(nasr: number, bamford: number, hoopLabel: string): void {
    const el = document.createElement('div')
    el.style.cssText = `
      position:absolute; top:8px; left:50%; transform:translateX(-50%);
      background:rgba(10,10,10,0.85); border:1px solid #444;
      padding:6px 20px; border-radius:4px; text-align:center;
      pointer-events:none;
    `
    el.innerHTML = `
      <div style="display:flex;gap:32px;align-items:center;margin-bottom:2px">
        <div style="text-align:center">
          <div style="font-size:10px;color:#ff6666;font-weight:700;letter-spacing:1px">NASR (EGY)</div>
          <div style="font-size:26px;color:#ff6666;font-weight:700;line-height:1">${nasr}</div>
        </div>
        <div style="color:#666;font-size:18px">–</div>
        <div style="text-align:center">
          <div style="font-size:10px;color:#66aaff;font-weight:700;letter-spacing:1px">BAMFORD (RSA)</div>
          <div style="font-size:26px;color:#66aaff;font-weight:700;line-height:1">${bamford}</div>
        </div>
      </div>
      <div style="font-size:8px;color:#555;letter-spacing:1px">GC WORLD CHAMPIONSHIP FINAL • CAIRO 2013</div>
      <div style="font-size:10px;color:#c8a415;margin-top:3px">${hoopLabel}</div>
    `
    this._root.appendChild(el)
  }

  showCommentary(lines: string[], onDismiss: () => void): void {
    const el = document.createElement('div')
    el.style.cssText = `
      position:absolute; top:50%; left:50%; transform:translate(-50%,-50%);
      background:rgba(0,0,0,0.88); border:2px solid #c8a415;
      padding:24px 36px; border-radius:6px; text-align:center;
      max-width:400px; pointer-events:all; cursor:pointer;
    `
    const text = lines.map(l => `<div style="font-size:16px;color:#fff;font-family:Georgia,serif;margin-bottom:8px">${l.replace(/\n/g, '<br>')}</div>`).join('')
    el.innerHTML = `${text}<div style="font-size:12px;color:#ffcc00;margin-top:12px">Click to take your shot →</div>`
    el.addEventListener('click', () => {
      el.remove()
      onDismiss()
    })
    this._root.appendChild(el)
  }

  showInOff(bamford: number, nasr: number, onContinue: () => void): void {
    const el = document.createElement('div')
    el.style.cssText = `
      position:absolute; inset:0;
      background:rgba(0,0,0,0.93);
      display:flex; flex-direction:column; align-items:center; justify-content:center;
      pointer-events:all; cursor:pointer;
    `
    el.innerHTML = `
      <style>
        @keyframes inoff-pulse { 0%,100%{transform:scale(1) rotate(-1deg)} 50%{transform:scale(1.06) rotate(1deg)} }
        @keyframes inoff-glow  { 0%,100%{text-shadow:0 0 20px #ffd700,0 0 40px #ffd700} 50%{text-shadow:0 0 60px #fff,0 0 100px #ffd700} }
      </style>
      <div style="font-size:11px;color:#c8a415;letter-spacing:5px;font-weight:700;margin-bottom:14px">✦ BRILLIANT SHOT ✦</div>
      <div style="font-size:88px;color:#ffd700;font-family:Georgia,serif;font-weight:700;line-height:1;
                  animation:inoff-pulse 0.9s ease-in-out infinite, inoff-glow 1.2s ease-in-out infinite">
        IN-OFF!
      </div>
      <div style="font-size:18px;color:#fff;font-family:Georgia,serif;margin-top:22px;text-align:center;
                  line-height:1.6;max-width:380px">
        Bamford cannons off Nasr's ball<br>and guides it straight through the hoop!
      </div>
      <div style="margin-top:28px;display:flex;gap:44px;align-items:center">
        <div style="text-align:center">
          <div style="font-size:10px;color:#ff6666;letter-spacing:1px;font-weight:700">NASR</div>
          <div style="font-size:52px;color:#ff6666;font-weight:700;line-height:1">${nasr}</div>
        </div>
        <div style="font-size:28px;color:#444">–</div>
        <div style="text-align:center">
          <div style="font-size:10px;color:#66aaff;letter-spacing:1px;font-weight:700">BAMFORD</div>
          <div style="font-size:52px;color:#66aaff;font-weight:700;line-height:1">${bamford}</div>
        </div>
      </div>
      <div style="font-size:12px;color:#666;margin-top:32px;letter-spacing:1px">Click to continue →</div>
    `
    el.addEventListener('click', () => { el.remove(); onContinue() })
    this._root.appendChild(el)
  }

  showClearanceCommentary(lines: string[], onDismiss: () => void): void {
    const el = document.createElement('div')
    el.style.cssText = `
      position:absolute; top:50%; left:50%; transform:translate(-50%,-50%);
      background:rgba(40,0,0,0.92); border:2px solid #ff4444;
      padding:24px 36px; border-radius:6px; text-align:center;
      max-width:400px; pointer-events:all; cursor:pointer;
    `
    const text = lines.map(l => `<div style="font-size:16px;color:#fff;font-family:Georgia,serif;margin-bottom:8px">${l.replace(/\n/g, '<br>')}</div>`).join('')
    el.innerHTML = `
      <div style="font-size:11px;color:#ff4444;font-weight:700;letter-spacing:2px;margin-bottom:10px">⚠ CLEARANCE</div>
      ${text}
      <div style="font-size:12px;color:#ff8888;margin-top:12px">Click to take the clearance shot →</div>
    `
    el.addEventListener('click', () => { el.remove(); onDismiss() })
    this._root.appendChild(el)
  }

  showResult(text: string, color: string): void {
    const el = document.createElement('div')
    el.style.cssText = `
      position:absolute; top:50%; left:50%; transform:translate(-50%,-50%);
      background:rgba(0,0,0,0.7); padding:14px 28px; border-radius:4px;
      font-size:28px; font-weight:700; color:${color};
      font-family:Georgia,serif; text-align:center; pointer-events:none;
    `
    el.textContent = text
    this._root.appendChild(el)
  }

  showAimInstruction(): void {
    const el = document.createElement('div')
    el.id = 'aim-instruction'
    el.style.cssText = `
      position:absolute; bottom:16px; left:50%; transform:translateX(-50%);
      color:#ffcc00; font-size:13px; font-weight:600; pointer-events:none;
      text-shadow: 0 1px 3px rgba(0,0,0,0.8);
    `
    el.textContent = 'Click and drag to aim • release to shoot'
    this._root.appendChild(el)
  }

  clearAimInstruction(): void {
    document.getElementById('aim-instruction')?.remove()
  }
}
